import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  Badge,
  DisplayFormattedDate,
  EmptyIndicator,
  MoreOptions,
  Pagination,
  User,
  UserPathStepProgress
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';
import {
  PathCompletionStatus,
  PathParticipantDeleteCompletion,
  PathParticipantsHeaderContent,
  PathParticipantsSubHeader,
  PathStepProgressStatus
} from '.';

import type { GetPathParticipantsRes } from 'shared-types/responses';
import type { ProgressData, User as UserInterface } from 'shared-types';

export type PathUser = UserInterface & {
  addedToPath: string;
  progressData: ProgressData;
};

const PathUsers = () => {
  const router = useRouter();

  const {
    language,
    messages,
    pageState: { path }
  } = useReduxState();

  const queryKeys = Object.keys(router.query);

  const { data, loading, updateData } = useApiData<GetPathParticipantsRes>({
    url:
      `/admin/paths/${path.id}/participants` +
      (queryKeys.length > 0
        ? `?${queryKeys
            .filter((key) => key !== 'view')
            .map((key) => `${key}=${router.query[key]}`)
            .join('&')}`
        : '')
  });

  const currentView = router.query.view || 'default';

  const showMenuColumn = data?.users?.some((user) =>
    Boolean(user.progressData?.pathCompletion)
  );

  return (
    <div className="users">
      <Box
        headerContent={
          <PathParticipantsHeaderContent
            userIds={data?.validUserIds || []}
            userSelectionParams={{
              excludeUsersWithoutEmail: true,
              routerQueryParams: {
                selectedUserIds: JSON.stringify(
                  data?.validUserIds ? Object.values(data.validUserIds) : []
                )
              },
              search: (router.query.search as string) || '',
              userSelector: 'emails'
            }}
            updateUsers={updateData}
          />
        }
        headerContentWrap={true}
        loading={loading}
        subHeaderContent={<PathParticipantsSubHeader />}
        padding={0}
        title={
          <>
            {messages.commonUsers}

            <StatCount
              isLarge={true}
              style={{ display: 'inline-block', marginLeft: 5 }}
              value={data?.validUserIds.length || 0}
            />
          </>
        }
      >
        {data?.users && data?.users.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell sticky={true}>{messages.commonUser}</BoxTableCell>

                {currentView === 'default' ? (
                  <>
                    <BoxTableCell>{messages.commonAdded}</BoxTableCell>
                    <BoxTableCell>{messages.commonProgress}</BoxTableCell>
                  </>
                ) : (
                  data?.steps.map((step, index) => (
                    <BoxTableCell
                      key={index}
                      valueContainerStyle={{ justifyContent: 'center' }}
                    >
                      {index + 1}
                    </BoxTableCell>
                  ))
                )}
                <BoxTableCell>{messages.commonCompleted}</BoxTableCell>
                {showMenuColumn ? (
                  <BoxTableCell
                    shrink={true}
                    sticky={true}
                    stickyAnchor="right"
                  ></BoxTableCell>
                ) : null}
              </BoxTableHeader>

              {data?.users.map((user) => (
                <BoxTableRow key={user.id}>
                  <BoxTableCell sticky={true} stickyBackgroundColor="white">
                    <User
                      user={user}
                      enableProfileLink={true}
                      linkAs={`/admin/paths/${path.id}/users/${user.id}`}
                      linkHref="/admin/paths/[pathId]/users/[userId]"
                      style={{ paddingRight: 20, minWidth: 200 }}
                    />
                  </BoxTableCell>

                  {currentView === 'steps' ? (
                    data?.steps.map((step, index) => {
                      const stepProgress = user.progressData.steps[step.id];
                      return (
                        <BoxTableCell
                          key={step.id}
                          valueContainerStyle={{ justifyContent: 'center' }}
                        >
                          <div className="step-progress">
                            <PathStepProgressStatus
                              iconOnly={true}
                              id={`path-participant-step-progress-${user.id}-${index}`}
                              items={step.items}
                              pathCompletion={user.progressData.pathCompletion}
                              progressData={stepProgress}
                              progressDataByItem={
                                stepProgress?.itemProgressByTypeId
                              }
                            />
                          </div>
                        </BoxTableCell>
                      );
                    })
                  ) : (
                    <>
                      <BoxTableCell label={messages.commonAdded}>
                        {user.addedToPath ? (
                          <DisplayFormattedDate date={user.addedToPath} />
                        ) : (
                          <Badge
                            isRounded={false}
                            text={messages.pathsPreviouslyAdded}
                          />
                        )}
                      </BoxTableCell>

                      <BoxTableCell label={messages.commonProgress}>
                        <UserPathStepProgress
                          progressData={user.progressData}
                        />
                      </BoxTableCell>
                    </>
                  )}
                  <BoxTableCell label={messages.commonCompleted}>
                    {user.progressData.pathCompletion ? (
                      <PathCompletionStatus
                        pathCompletion={user.progressData.pathCompletion}
                        showDate={true}
                      />
                    ) : null}
                  </BoxTableCell>

                  {showMenuColumn ? (
                    <BoxTableCell
                      shrink={true}
                      sticky={true}
                      stickyAnchor="right"
                      stickyBackgroundColor="white"
                    >
                      {user.progressData.pathCompletion ? (
                        <MoreOptions
                          renderInPortal={true}
                          styleContentButtons={false}
                        >
                          <ApiDownloadButton
                            filename={`${user.progressData.pathCompletion.pathTitle}.pdf`}
                            icon="file-pdf"
                            isDropdown={true}
                            text={messages.completionsDownloadCertificate}
                            url={
                              `/admin/path-certificate` +
                              `?completionId=${user.progressData.pathCompletion.id}` +
                              `&language=${language}`
                            }
                          />
                          <PathParticipantDeleteCompletion
                            pathId={path.id}
                            updateUsers={updateData}
                            userId={user.id}
                          />
                        </MoreOptions>
                      ) : null}
                    </BoxTableCell>
                  ) : null}
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="user" title={messages.commonNoUsers} />
        )}
      </Box>

      <Pagination
        currentPage={router.query.page ? Number(router.query.page) : 1}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => {
          router.replace({ query: { ...router.query, page } });
        }}
      />
    </div>
  );
};

export default PathUsers;
